import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../api/axiosConfig';
import { apiErrorText } from '../utils/apiErrors';
import ExportPreviewModal from '../components/ExportPreviewModal';
import { hasNavPermission } from '../components/ProtectedRoute';
import { useAuth } from '../context/AuthContext';
import { isoWeekYear } from '../utils/dates';
import './CompaniesPage.css';
import './SalesFormPage.css';
import '../styles/topbarButtons.css';
import '../styles/productionSummary.css';
import '../styles/pageHeader.css';

// Resumen de Producción (Producción), read-only: two tables with one row per farm and one column
// per week. CAJAS compares producidas (Detalles de Cosecha) with the presupuestadas (typed in on
// the Presupuesto de Cajas page, BudgetPage.js) as a % cumplimiento; ENFUNDE has the week's
// Control de Enfunde. Only registered weeks show (a budget, harvest or enfunde sheet), and the
// Desde / Hasta filters only offer those; by default the year's last registered weeks.
// Data: GET production-summary/ (core/production_summary.py).

const COMPANY_KEY = 'farmershub_summary_company';

const fmt = (n) => (n == null ? '' : Number(n).toLocaleString('es-EC'));
const sum = (values) => values.reduce((a, v) => a + (v || 0), 0);
const addUp = (cells) => (cells.some((v) => v != null) ? sum(cells) : null);

// Columns never get narrower than these (px): with many weeks the table scrolls sideways instead.
// Both tables use the same week width, so their weeks line up.
const FARM_COL = 120;
const WEEK_COL = 100;
const SCROLL_TITLE_AT = 900; // wider tables keep their title at the left, in view while scrolling

// % cumplimiento = producidas ÷ presupuestadas. Only weeks that already have production count
// (a budget for a week still to come doesn't pull the % down), same as the difference.
const CUMPL_GOOD = 1; // ≥ 100 %: green
const CUMPL_WARN = 0.9; // 90–99 %: yellow; below: red

function cumplimiento(pairs) {
  const produced = pairs.filter((p) => p.produced != null);
  if (!produced.length) return null;
  const prod = sum(produced.map((p) => p.produced));
  const budget = sum(produced.map((p) => p.budget || 0));
  return { prod, budget, diff: prod - budget, pct: budget > 0 ? prod / budget : null };
}

const cumplClass = (c) => {
  if (!c || c.pct == null) return '';
  if (c.pct >= CUMPL_GOOD) return 'ps-cumpl-good';
  return c.pct >= CUMPL_WARN ? 'ps-cumpl-warn' : 'ps-cumpl-bad';
};
const fmtPct = (pct) => (pct == null ? '' : `${Math.round(pct * 100)}%`);
const fmtDiff = (diff) => (diff > 0 ? `+${fmt(diff)}` : fmt(diff));

// A light version of a cinta color, as a cell background that keeps the numbers readable.
function tint(hex) {
  const n = parseInt(hex.replace('#', ''), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, 0.35)`;
}

// Black or white text, whichever reads better on the cinta color.
function textOn(hex) {
  const n = parseInt(hex.replace('#', ''), 16);
  const luminance = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return luminance > 0.55 ? '#000' : '#fff';
}

// The ribbons used in a week (one per farm sheet; usually all the same).
function weekRibbons(data, farms, w) {
  const seen = new Map();
  farms.forEach((f) => {
    const c = data.cinta[f.id]?.[w];
    if (c && !seen.has(c.color)) seen.set(c.color, c);
  });
  return [...seen.values()];
}

function TableTitle({ title, legend, span, minWidth }) {
  return (
    <tr>
      <th className={`ps-title${minWidth > SCROLL_TITLE_AT ? ' ps-title-scroll' : ''}`} colSpan={span}>
        <span className="ps-title-text">
          {title}
          {legend && <span className="ps-legend">{legend}</span>}
        </span>
      </th>
    </tr>
  );
}

const CAJAS_LEGEND = (
  <>
    <span className="ps-legend-key ps-cumpl-good">≥ 100%</span>
    <span className="ps-legend-key ps-cumpl-warn">90–99%</span>
    <span className="ps-legend-key ps-cumpl-bad">&lt; 90%</span>
  </>
);

// Cajas: one cell per week (and one for the range's total): producidas in large type with the
// % cumplimiento and the difference beside it, the cell colored by the cumplimiento; hovering the
// % / difference shows the presupuestadas. A week with a budget but no production yet only shows
// its budget. The last row adds up every farm.
function CajasTable({ farms, weeks, values }) {
  const minWidth = FARM_COL + (weeks.length + 1) * WEEK_COL;
  const pair = (f, w) => ({ budget: values.budget(f.id, w), produced: values.produced(f.id, w) });
  const columns = [
    ...weeks.map((w) => ({ key: w, label: `Sem. ${w}`, pairs: (fs) => fs.map((f) => pair(f, w)) })),
    { key: 'total', label: 'Total', total: true, pairs: (fs) => fs.flatMap((f) => weeks.map((w) => pair(f, w))) },
  ];
  const cell = (col, fs) => {
    const pairs = col.pairs(fs);
    const c = cumplimiento(pairs);
    const budget = addUp(pairs.map((p) => p.budget));
    const produced = addUp(pairs.map((p) => p.produced));
    const className = `ps-week-cell ${cumplClass(c)}${col.total ? ' ps-total-col' : ''}`;
    if (produced == null && budget == null) return <td key={col.key} className={className} />;
    if (produced == null) {
      return (
        <td key={col.key} className={className}>
          <div className="ps-cell-budget"><span className="ps-cell-label">Presup.</span> {fmt(budget)}</div>
        </td>
      );
    }
    return (
      <td key={col.key} className={className}>
        <div className="ps-cell-top">
          <span className="ps-prod">{fmt(produced)}</span>
          <span className="ps-cell-pct ps-has-tip" tabIndex={0}>
            {c.pct != null && fmtPct(c.pct)}
            <span className="ps-cell-diff">{fmtDiff(c.diff)}</span>
            {/* The budget the % compares with (farms and weeks that already produced), and the
                whole budget when it's more than that. */}
            <span className="ps-tip" role="tooltip">
              Presupuesto: {c.budget ? fmt(c.budget) : 'sin presupuesto'}
              {budget != null && budget !== c.budget && (
                <span className="ps-tip-note">Total: {fmt(budget)} (con semanas o fincas sin producción)</span>
              )}
            </span>
          </span>
        </div>
      </td>
    );
  };
  return (
    <div className="companies-card ps-block">
      <table className="ps-table ps-cajas" style={{ minWidth }}>
        {/* Explicit widths: with a fixed layout the spanning title row would otherwise size them. */}
        <colgroup>
          <col style={{ width: FARM_COL }} />
          {columns.map((col) => <col key={col.key} />)}
        </colgroup>
        <thead>
          <TableTitle title="CAJAS" legend={CAJAS_LEGEND} span={columns.length + 1} minWidth={minWidth} />
          <tr>
            <th className="ps-farm-col ps-week-head">Finca</th>
            {columns.map((col) => <th key={col.key} className="ps-farm-head">{col.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {farms.map((f) => (
            <tr key={f.id}>
              <td className="ps-farm-col" title={f.name}>{f.name}</td>
              {columns.map((col) => cell(col, [f]))}
            </tr>
          ))}
          <tr className="ps-total">
            <td className="ps-farm-col">Total</td>
            {columns.map((col) => cell(col, farms))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// Enfunde: one column per week, as wide as the cajas week above it so the weeks line up; each
// cell tinted with that farm's cinta. The cinta names show when hovering the week (always, with
// `inlineNotes`, in the export preview).
function EnfundeTable({ data, farms, weeks, values, inlineNotes }) {
  const minWidth = FARM_COL + (weeks.length + 1) * WEEK_COL;
  const value = values.enfunde;
  const weekTotals = weeks.map((w) => addUp(farms.map((f) => value(f.id, w))));
  const headNote = (w) => weekRibbons(data, farms, w).map((c) => (
    <span
      key={c.color} className="ps-chip"
      style={c.hex_color ? { background: c.hex_color, color: textOn(c.hex_color) } : undefined}
    >
      {c.color}
    </span>
  ));
  const cellStyle = (f, w) => {
    const c = data.cinta[f.id]?.[w];
    return c?.hex_color ? { background: tint(c.hex_color) } : undefined;
  };
  return (
    <div className="companies-card ps-block">
      <table className="ps-table" style={{ minWidth }}>
        <colgroup>
          <col style={{ width: FARM_COL }} />
          {weeks.map((w) => <col key={w} />)}
          <col />
        </colgroup>
        <thead>
          <TableTitle title="ENFUNDE" span={weeks.length + 2} minWidth={minWidth} />
          <tr>
            <th className="ps-farm-col ps-week-head">Finca</th>
            {weeks.map((w) => {
              const note = headNote(w);
              if (note.length && inlineNotes) {
                return <th key={w} className="ps-farm-head">Sem. {w}<div className="ps-head-note-inline">{note}</div></th>;
              }
              return note.length ? (
                <th key={w} className="ps-farm-head ps-has-note" tabIndex={0}>
                  <span className="ps-head-label">Sem. {w}</span>
                  <div className="ps-head-note" role="tooltip">{note}</div>
                </th>
              ) : (
                <th key={w} className="ps-farm-head">Sem. {w}</th>
              );
            })}
            <th className="ps-farm-head">Total</th>
          </tr>
        </thead>
        <tbody>
          {farms.map((f) => (
            <tr key={f.id}>
              <td className="ps-farm-col" title={f.name}>{f.name}</td>
              {weeks.map((w) => <td key={w} className="ps-num" style={cellStyle(f, w)}>{fmt(value(f.id, w))}</td>)}
              <td className="ps-num ps-total-col">{fmt(addUp(weeks.map((w) => value(f.id, w))))}</td>
            </tr>
          ))}
          <tr className="ps-total">
            <td className="ps-farm-col">Total</td>
            {weekTotals.map((v, i) => <td key={weeks[i]} className="ps-num">{fmt(v)}</td>)}
            <td className="ps-num ps-total-col">{fmt(addUp(weekTotals))}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// Both tables, one under the other. In the export preview (`printable`) everything is read-only.
function SummaryTables({ data, farms, weeks, values, printable }) {
  const gridRef = useRef(null);

  // Scrolling one table sideways scrolls the other too, so the same weeks stay lined up.
  // (A native capturing listener: scroll events don't bubble to React's onScroll.)
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid || printable) return undefined;
    const onScroll = (e) => {
      const card = e.target;
      if (!card.classList?.contains('ps-block')) return;
      grid.querySelectorAll('.ps-block').forEach((other) => {
        if (other !== card && other.scrollLeft !== card.scrollLeft) other.scrollLeft = card.scrollLeft;
      });
    };
    grid.addEventListener('scroll', onScroll, true);
    return () => grid.removeEventListener('scroll', onScroll, true);
  }, [printable]);

  return (
    <div ref={gridRef} className={printable ? 'ps-print' : 'ps-grid'}>
      <CajasTable farms={farms} weeks={weeks} values={values} />
      <EnfundeTable data={data} farms={farms} weeks={weeks} values={values} inlineNotes={printable} />
    </div>
  );
}

// ---- Excel / PDF: the same two tables, one under the other ----
const GREEN = '2F7D32';
const LIGHT_GREEN = 'C8E6C9';
// The cumplimiento colors of the page, as solid fills.
const CUMPL_FILLS = { 'ps-cumpl-good': '92D050', 'ps-cumpl-warn': 'FFFF00', 'ps-cumpl-bad': 'FF9E9E' };

const hex6 = (hex) => (hex ? hex.replace('#', '').slice(0, 6).toUpperCase() : null);

// The cinta color mixed with white (about what tint() looks like on the page), for solid fills.
function lighten(hex, amount = 0.65) {
  const n = parseInt(hex, 16);
  return [16, 8, 0].map((shift) => {
    const c = (n >> shift) & 255;
    return Math.round(c + (255 - c) * amount).toString(16).padStart(2, '0');
  }).join('').toUpperCase();
}

// Each table as plain values plus fill colors, shared by the Excel and PDF files: `groups` are the
// week headers (+ Total) and `subLabels` the columns under each one (none for enfunde).
function exportSections(data, farms, weeks, values) {
  const weekTotal = (key, w) => addUp(farms.map((f) => values[key](f.id, w)));
  const farmTotal = (key, f) => addUp(weeks.map((w) => values[key](f.id, w)));

  // Cajas: per week (and for the range) Presup. | Prod. | Dif. | %, the last two colored by
  // cumplimiento as on the page.
  const pair = (f, w) => ({ budget: values.budget(f.id, w), produced: values.produced(f.id, w) });
  const groupValues = (pairs) => {
    const c = cumplimiento(pairs);
    const fill = CUMPL_FILLS[cumplClass(c)] || null;
    return {
      cells: [addUp(pairs.map((p) => p.budget)), addUp(pairs.map((p) => p.produced)), c ? c.diff : null, c ? c.pct : null],
      fills: [null, null, fill, fill],
    };
  };
  const cajasRow = (name, pairsOf, bold) => {
    const parts = [...weeks.map((w) => groupValues(pairsOf([w]))), groupValues(pairsOf(weeks))];
    return {
      cells: [name, ...parts.flatMap((g) => g.cells)],
      fills: [null, ...parts.flatMap((g) => g.fills)],
      bold,
    };
  };
  const cajas = {
    title: 'CAJAS',
    groups: [...weeks.map((w) => `Sem. ${w}`), 'Total'],
    subLabels: ['Presup.', 'Prod.', 'Dif.', '% Cumpl.'],
    kinds: ['num', 'num', 'diff', 'pct'], // how each sub-column is formatted
    rows: [
      ...farms.map((f) => cajasRow(f.name, (ws) => ws.map((w) => pair(f, w)))),
      cajasRow('Total', (ws) => farms.flatMap((f) => ws.map((w) => pair(f, w))), true),
    ],
  };

  // The PDF shows cajas as on the page: one cell per week, «producidas / de presupuestadas / % dif.».
  const compactCell = (pairs) => {
    const c = cumplimiento(pairs);
    const budget = c ? c.budget : addUp(pairs.map((p) => p.budget));
    const produced = addUp(pairs.map((p) => p.produced));
    const lines = [produced == null ? '—' : fmt(produced), `de ${budget == null ? '—' : fmt(budget)}`];
    if (c) lines.push([c.pct != null && fmtPct(c.pct), fmtDiff(c.diff)].filter(Boolean).join('  '));
    return { text: lines.join('\n'), fill: CUMPL_FILLS[cumplClass(c)] || null };
  };
  const compactRow = (name, pairsOf, bold) => {
    const parts = [...weeks.map((w) => compactCell(pairsOf([w]))), compactCell(pairsOf(weeks))];
    return { cells: [name, ...parts.map((p) => p.text)], fills: [null, ...parts.map((p) => p.fill)], bold };
  };
  const cajasCompact = {
    title: 'CAJAS',
    groups: [...weeks.map((w) => `Sem. ${w}`), 'Total'],
    subLabels: [],
    kinds: ['text'],
    rows: [
      ...farms.map((f) => compactRow(f.name, (ws) => ws.map((w) => pair(f, w)))),
      compactRow('Total', (ws) => farms.flatMap((f) => ws.map((w) => pair(f, w))), true),
    ],
  };

  const ribbonNames = (w) => weekRibbons(data, farms, w).map((c) => c.color).join(' / ');
  const cintaFill = (f, w) => {
    const hex = hex6(data.cinta[f.id]?.[w]?.hex_color);
    return hex ? lighten(hex) : null;
  };
  const enfundeTotals = weeks.map((w) => weekTotal('enfunde', w));
  const enfunde = {
    title: 'ENFUNDE',
    groups: [...weeks.map((w) => (ribbonNames(w) ? `Sem. ${w}\n${ribbonNames(w)}` : `Sem. ${w}`)), 'Total'],
    subLabels: [],
    kinds: ['num'],
    rows: [
      ...farms.map((f) => ({
        cells: [f.name, ...weeks.map((w) => values.enfunde(f.id, w)), farmTotal('enfunde', f)],
        fills: [null, ...weeks.map((w) => cintaFill(f, w)), null],
      })),
      { cells: ['Total', ...enfundeTotals, addUp(enfundeTotals)], fills: [], bold: true },
    ],
  };
  // Excel keeps separate numeric columns (Presup. | Prod. | Dif. | %) so they can be calculated with.
  return { excel: [cajas, enfunde], pdf: [cajasCompact, enfunde] };
}

// The kind of a data column ('num', 'diff' or 'pct'), from its position inside its group.
const kindOf = (section, col) => section.kinds[(col - 1) % section.kinds.length];
const NUM_FORMATS = { num: '#,##0', diff: '+#,##0;-#,##0;0', pct: '0%' };
const showValue = (kind, v) => {
  if (v == null || v === '') return '';
  if (typeof v !== 'number') return v;
  if (kind === 'pct') return fmtPct(v);
  return kind === 'diff' ? fmtDiff(v) : fmt(v);
};

const columnsPerGroup = (section) => Math.max(1, section.subLabels.length);
const sectionWidth = (section) => 1 + section.groups.length * columnsPerGroup(section);

async function exportExcel(title, fileName, sections) {
  const workbook = new ExcelJS.Workbook();
  // Farm names frozen, like the sticky column on the page.
  const sheet = workbook.addWorksheet('Resumen', {
    views: [{ state: 'frozen', xSplit: 1 }],
    // Prints horizontally, every column on the page's width (as the PDF).
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });
  const thin = { style: 'thin', color: { argb: 'FF000000' } };
  const medium = { style: 'medium', color: { argb: 'FF000000' } };
  const solid = (hex) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${hex}` } });
  const widest = Math.max(...sections.map(sectionWidth));

  sheet.mergeCells(1, 1, 1, widest);
  sheet.getCell(1, 1).value = title;
  sheet.getCell(1, 1).font = { size: 14, bold: true };
  sheet.addRow([]);
  sections.forEach((section) => {
    const { title: tableTitle, groups, subLabels, rows } = section;
    const per = columnsPerGroup(section);
    const width = sectionWidth(section);
    // A heavier line before each week's group of columns.
    const borderFor = (col) => ({
      top: thin, bottom: thin, right: thin, left: per > 1 && col > 1 && (col - 2) % per === 0 ? medium : thin,
    });
    const styleHead = (row) => row.eachCell({ includeEmpty: true }, (c, col) => {
      if (col > width) return;
      c.fill = solid(LIGHT_GREEN);
      c.border = borderFor(col);
      c.font = { size: 9 };
      c.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    });

    const titleRow = sheet.addRow([tableTitle]);
    sheet.mergeCells(titleRow.number, 1, titleRow.number, width);
    const titleCell = titleRow.getCell(1);
    titleCell.fill = solid(GREEN);
    titleCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    titleCell.alignment = { horizontal: 'center' };

    const groupRow = sheet.addRow(['Finca', ...groups.flatMap((g) => [g, ...Array(per - 1).fill('')])]);
    if (per > 1) {
      const subRow = sheet.addRow(['', ...groups.flatMap(() => subLabels)]);
      sheet.mergeCells(groupRow.number, 1, subRow.number, 1);
      groups.forEach((g, i) => sheet.mergeCells(groupRow.number, 2 + i * per, groupRow.number, 1 + (i + 1) * per));
      styleHead(subRow);
    }
    styleHead(groupRow);

    rows.forEach(({ cells, fills, bold }) => {
      const row = sheet.addRow(cells.map((v) => (v == null ? '' : v)));
      row.eachCell({ includeEmpty: true }, (c, col) => {
        if (col > width) return;
        c.border = borderFor(col);
        c.font = { bold: !!bold || col > width - per };
        if (col > 1) {
          c.alignment = { horizontal: 'center' };
          c.numFmt = NUM_FORMATS[kindOf(section, col - 1)];
        }
        if (fills[col - 1]) c.fill = solid(fills[col - 1]);
      });
    });
    sheet.addRow([]);
  });
  sheet.getColumn(1).width = 22;
  for (let i = 2; i <= widest; i += 1) sheet.getColumn(i).width = 11;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileName}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function exportPDF(title, fileName, sections) {
  // Wide tables go on A3 with a smaller font so every column still fits across the page.
  const columns = Math.max(...sections.map(sectionWidth)) - 1;
  const wide = columns > 14;
  const fontSize = columns > 45 ? 4.5 : columns > 27 ? 5.5 : wide ? 6.5 : 8;
  const doc = new jsPDF({ orientation: 'landscape', format: wide ? 'a3' : 'a4' });
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(title, 14, 14);
  let y = 20;
  sections.forEach((section) => {
    const { title: tableTitle, groups, subLabels, rows } = section;
    const per = columnsPerGroup(section);
    const width = sectionWidth(section);
    const show = (v, col) => (col === 0 ? v : showValue(kindOf(section, col), v));
    const head = per > 1
      ? [
        [{ content: 'Finca', rowSpan: 2 }, ...groups.map((g) => ({ content: g, colSpan: per }))],
        groups.flatMap(() => subLabels),
      ]
      : [['Finca', ...groups]];
    autoTable(doc, {
      startY: y,
      theme: 'grid',
      head: [
        [{ content: tableTitle, colSpan: width, styles: { fillColor: `#${GREEN}`, textColor: '#ffffff', halign: 'center' } }],
        ...head,
      ],
      body: rows.map(({ cells }) => cells.map(show)),
      styles: { fontSize, cellPadding: wide ? 0.7 : 1.2, halign: 'center', lineColor: [0, 0, 0], lineWidth: 0.1, textColor: [0, 0, 0] },
      headStyles: { fillColor: `#${LIGHT_GREEN}`, textColor: [0, 0, 0], fontStyle: 'normal', valign: 'middle' },
      columnStyles: { 0: { halign: 'left', cellWidth: wide ? 28 : 38 } },
      didParseCell: (cell) => {
        if (cell.section !== 'body') return;
        const row = rows[cell.row.index];
        const fillHex = row.fills[cell.column.index];
        if (fillHex) cell.cell.styles.fillColor = `#${fillHex}`;
        if (row.bold || cell.column.index >= width - per) cell.cell.styles.fontStyle = 'bold';
      },
    });
    y = doc.lastAutoTable.finalY + 8;
  });
  doc.save(`${fileName}.pdf`);
}

export default function ProductionSummaryPage() {
  const { user } = useAuth();
  const thisYear = isoWeekYear(new Date());
  const [allFarms, setAllFarms] = useState([]);
  const [companyId, setCompanyId] = useState(() => {
    try { return localStorage.getItem(COMPANY_KEY) || ''; } catch { return ''; }
  }); // '' = Todas las empresas
  const [year, setYear] = useState(thisYear);
  // The chosen range; null = the server's default (the year's last registered weeks).
  const [range, setRange] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(false);

  const companies = useMemo(() => {
    const byId = new Map(allFarms.map((f) => [String(f.company), f.company_name]));
    return [...byId].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [allFarms]);

  useEffect(() => {
    api.get('farms/').then((r) => setAllFarms(r.data)).catch(() => { /* the summary still loads */ });
  }, []);

  useEffect(() => {
    if (companyId && allFarms.length && !companies.some((c) => c.id === companyId)) {
      setCompanyId('');
      setRange(null);
    }
  }, [allFarms.length, companies, companyId]);

  useEffect(() => {
    try { localStorage.setItem(COMPANY_KEY, companyId); } catch { /* ignore */ }
    let stale = false; // a newer request replaced this one
    setLoading(true);
    setStatus(null);
    api.get('production-summary/', {
      params: {
        year, ...(range ? { week_from: range.from, week_to: range.to } : {}), ...(companyId ? { company: companyId } : {}),
      },
    })
      .then((r) => { if (!stale) setData(r.data); })
      .catch((err) => {
        if (stale) return;
        setData(null);
        setStatus(apiErrorText(err, 'No se pudo cargar el resumen.'));
      })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [year, range, companyId]);

  const farms = data?.farms || [];
  const weeks = data?.weeks || [];
  const values = {
    budget: (farmId, w) => data?.budget[farmId]?.[w] ?? null,
    produced: (farmId, w) => data?.produced[farmId]?.[w] ?? null,
    enfunde: (farmId, w) => data?.enfunde[farmId]?.[w] ?? null,
  };

  const weekOptions = data?.year_weeks || [];
  const weekFrom = range?.from ?? data?.week_from ?? '';
  const weekTo = range?.to ?? data?.week_to ?? '';
  const pickRange = (from, to) => setRange(from <= to ? { from, to } : { from: to, to: from });
  const yearOptions = [thisYear + 1, thisYear, thisYear - 1, thisYear - 2];

  const companyName = companies.find((c) => c.id === companyId)?.name;
  const exportTitle = data
    ? `Resumen de Producción ${data.year} · Semanas ${data.week_from}–${data.week_to}${companyName ? ` · ${companyName}` : ''}`
    : '';
  const exportFile = data
    ? `Resumen_Produccion_${data.year}_S${data.week_from}-${data.week_to}${companyName ? `_${companyName.replace(/\s+/g, '_')}` : ''}`
    : '';

  return (
    <div className="companies-root">
      <div className="page-topbar">
        <Link to="/production" className="topbar-btn">← Producción</Link>
        <div className="ps-topbar-actions">
          {hasNavPermission(user, 'production_budget') && (
            <Link to="/production-budget" className="topbar-btn">✏️ Presupuesto</Link>
          )}
          <button
            type="button" className="topbar-btn topbar-btn-primary" onClick={() => setPreview(true)}
            disabled={!weeks.length || !farms.length}
          >
            📤 Exportar
          </button>
        </div>
      </div>
      <h1 className="companies-title">📊 Resumen de Producción</h1>
      <p className="ps-subtitle page-header-divided">
        Cajas producidas (Detalles de Cosecha) frente a las presupuestadas, con su cumplimiento, y el
        enfunde de Control de Enfunde, por semana y finca.
      </p>

      <div className="history-filters">
        {companies.length > 1 && (
          <select className="history-select" value={companyId} onChange={(e) => { setCompanyId(e.target.value); setRange(null); }} aria-label="Empresa">
            <option value="">Todas las empresas</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <select className="history-select" value={year} onChange={(e) => { setYear(Number(e.target.value)); setRange(null); }} aria-label="Año">
          {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        {weekOptions.length > 0 && (
          <>
            <select
              className="history-select" value={weekFrom} aria-label="Desde semana"
              onChange={(e) => pickRange(Number(e.target.value), Math.max(Number(e.target.value), Number(weekTo)))}
            >
              {weekOptions.map((w) => <option key={w} value={w}>Desde semana {w}</option>)}
            </select>
            <select
              className="history-select" value={weekTo} aria-label="Hasta semana"
              onChange={(e) => pickRange(Math.min(Number(e.target.value), Number(weekFrom)), Number(e.target.value))}
            >
              {weekOptions.map((w) => <option key={w} value={w}>Hasta semana {w}</option>)}
            </select>
          </>
        )}
      </div>

      {status && <div className="companies-status companies-status-error">{status}</div>}

      {loading && !data ? (
        <div className="companies-card"><div className="companies-empty">Cargando…</div></div>
      ) : data && farms.length === 0 ? (
        <div className="companies-card"><div className="companies-empty">No hay fincas para mostrar.</div></div>
      ) : data && weeks.length === 0 ? (
        <div className="companies-card">
          <div className="companies-empty">No hay semanas registradas en {data.year}.</div>
        </div>
      ) : data && (
        <SummaryTables data={data} farms={farms} weeks={weeks} values={values} />
      )}

      {preview && data && (
        <ExportPreviewModal
          title={exportTitle}
          fileName={exportFile}
          landscape
          onClose={() => setPreview(false)}
          onDownload={(format) => {
            const sections = exportSections(data, farms, weeks, values);
            return format === 'excel'
              ? exportExcel(exportTitle, exportFile, sections.excel)
              : exportPDF(exportTitle, exportFile, sections.pdf);
          }}
        >
          <div className="ps-print-title">{exportTitle}</div>
          <SummaryTables data={data} farms={farms} weeks={weeks} values={values} printable />
        </ExportPreviewModal>
      )}
    </div>
  );
}
